import React, { useState } from 'react';
import '../styles/CodeBlock.css';

interface Props {
  code: string;
  language?: string;
  title?: string;
}

export default function CodeBlock({ code, language = 'javascript', title }: Props) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Simple syntax highlighting (Phase 2 - can upgrade to Prism.js)
  const highlightCode = (code: string, lang: string) => {
    // For Phase 2, return code as-is with line numbers
    // Phase 3 can add real syntax highlighting via Prism.js
    return code;
  };

  const lines = code.split('\n');

  return (
    <div className="code-block">
      <div className="code-header">
        {title && <span className="code-title">{title}</span>}
        <span className="code-language">{language}</span>
        <button
          className="code-copy-button"
          onClick={handleCopy}
          title="Copy code"
        >
          {copied ? '✓ Copied' : 'Copy'}
        </button>
      </div>

      <pre className="code-content">
        <code className={`language-${language}`}>
          {lines.map((line, i) => (
            <div key={i} className="code-line">
              <span className="code-line-number">{i + 1}</span>
              <span className="code-line-text">{line || '\n'}</span>
            </div>
          ))}
        </code>
      </pre>
    </div>
  );
}

/**
 * Utility: Extract code blocks from markdown-like text
 * Format: ```language\ncode\n```
 */
export function extractCodeBlocks(text: string) {
  const codeBlockRegex = /```(\w+)?\n([\s\S]*?)```/g;
  const blocks: Array<{ language: string; code: string }> = [];
  let match;

  while ((match = codeBlockRegex.exec(text)) !== null) {
    blocks.push({
      language: match[1] || 'text',
      code: match[2].trim()
    });
  }

  return blocks;
}

/**
 * Utility: Replace code blocks with placeholder for rendering
 */
export function replaceCodeBlocks(text: string) {
  return text.replace(/```(\w+)?\n[\s\S]*?```/g, '[CODE_BLOCK]');
}
